/**
 * Theme hooks, extracted from routes/resources/theme-switch.tsx so root can
 * read the theme WITHOUT dragging conform+zod into every route's client
 * graph (perf budget gate: core route ≤180KB gz). The ThemeSwitch UI (which
 * legitimately uses conform) stays in the resource route.
 */
import { useFetchers } from 'react-router'
import { useHints, useOptionalHints } from '#app/utils/client-hints.tsx'
import {
	useOptionalRequestInfo,
	useRequestInfo,
} from '#app/utils/request-info.ts'

const MODES = ['system', 'light', 'dark'] as const
type Mode = (typeof MODES)[number]

/** The pending theme submission, read without a schema library. */
export function useOptimisticThemeMode(): Mode | undefined {
	const fetchers = useFetchers()
	const themeFetcher = fetchers.find(
		(f) => f.formAction === '/resources/theme-switch',
	)
	const value = themeFetcher?.formData?.get('theme')
	return typeof value === 'string' && (MODES as readonly string[]).includes(value)
		? (value as Mode)
		: undefined
}

/**
 * @returns the user's theme preference, or the client hint theme if the user
 * has not set a preference.
 */
export function useTheme() {
	const hints = useHints()
	const requestInfo = useRequestInfo()
	const optimisticMode = useOptimisticThemeMode()
	if (optimisticMode) {
		return optimisticMode === 'system' ? hints.theme : optimisticMode
	}
	return requestInfo.userPrefs.theme ?? hints.theme
}

export function useOptionalTheme() {
	const optionalHints = useOptionalHints()
	const optionalRequestInfo = useOptionalRequestInfo()
	const optimisticMode = useOptimisticThemeMode()
	if (optimisticMode) {
		return optimisticMode === 'system' ? optionalHints?.theme : optimisticMode
	}
	return optionalRequestInfo?.userPrefs.theme ?? optionalHints?.theme
}
